import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { OrganizationProvider } from '@/contexts/OrganizationContext'

// ─── Public pages only (lightweight, no auth needed) ────────────────────────
const PublicSpeakerForm = lazy(() => import('@/pages/public/PublicSpeakerForm').then(m => ({ default: m.PublicSpeakerForm })))
const PublicSponsorForm = lazy(() => import('@/pages/public/PublicSponsorForm').then(m => ({ default: m.PublicSponsorForm })))
const PublicSessionBooking = lazy(() => import('@/pages/public/PublicSessionBooking').then(m => ({ default: m.PublicSessionBooking })))
const PublicHeroCardForm = lazy(() => import('@/pages/public/PublicHeroCardForm').then(m => ({ default: m.PublicHeroCardForm })))
const QuoteSigningPage = lazy(() => import('@/pages/public/QuoteSigningPage').then(m => ({ default: m.QuoteSigningPage })))
const PortalDocumentSigningPage = lazy(() => import('@/pages/portal/PortalDocumentSigningPage').then(m => ({ default: m.PortalDocumentSigningPage })))
const RetainerContractSigningPage = lazy(() => import('@/pages/public/RetainerContractSigningPage').then(m => ({ default: m.RetainerContractSigningPage })))
const AgreementSigningPage = lazy(() => import('@/pages/public/AgreementSigningPage').then(m => ({ default: m.AgreementSigningPage })))
const PublicScheduleView = lazy(() => import('@/pages/public/PublicScheduleView'))
const ProfileVerificationPage = lazy(() => import('@/pages/public/ProfileVerificationPage').then(m => ({ default: m.ProfileVerificationPage })))
const MobileScanner = lazy(() => import('@/pages/public/MobileScanner').then(m => ({ default: m.MobileScanner })))
const PrivacyPolicy = lazy(() => import('@/pages/public/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })))
const TermsOfService = lazy(() => import('@/pages/public/TermsOfService').then(m => ({ default: m.TermsOfService })))
const StylePreview = lazy(() => import('@/pages/StylePreview').then(m => ({ default: m.StylePreview })))
const SharedFilePage = lazy(() => import('@/pages/public/SharedFilePage'))
const ContentReviewPage = lazy(() => import('@/pages/public/ContentReviewPage'))
const PublicTrackerPage = lazy(() => import('@/pages/public/PublicTrackerPage'))
const PublicSpeakerRosterView = lazy(() => import('@/pages/public/PublicSpeakerRosterView'))
const PublicSponsorRosterView = lazy(() => import('@/pages/public/PublicSponsorRosterView'))
const CrewOnboardingForm = lazy(() => import('@/pages/public/CrewOnboardingForm').then(m => ({ default: m.CrewOnboardingForm })))

// ─── Authenticated app (MUI, auth, all routes) — loaded only when needed ────
const AuthenticatedApp = lazy(() => import('./AuthenticatedApp'))

// Pure CSS loading fallback — zero external dependencies
function LoadingFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div style={{
        width: 32,
        height: 32,
        borderRadius: 6,
        border: '3px solid rgba(0, 0, 0, 0.1)',
        borderTopColor: '#6366f1',
        animation: 'prepro-spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes prepro-spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public routes — no auth, no MUI, fastest possible load */}
          <Route path="/submit/speaker/:accessCode" element={<PublicSpeakerForm />} />
          <Route path="/submit/sponsor/:accessCode" element={<PublicSponsorForm />} />
          <Route path="/submit/session/:accessCode" element={<PublicSessionBooking />} />
          <Route path="/submit/herocard/:accessCode" element={<PublicHeroCardForm />} />
          <Route path="/sign/:token" element={<QuoteSigningPage />} />
          <Route path="/sign-retainer/:token" element={<RetainerContractSigningPage />} />
          <Route path="/sign-agreement/:token" element={<AgreementSigningPage />} />
          <Route path="/public/schedule/:shareCode" element={<PublicScheduleView />} />
          <Route path="/public/speakers/:shareCode" element={<PublicSpeakerRosterView />} />
          <Route path="/public/sponsors/:shareCode" element={<PublicSponsorRosterView />} />
          <Route path="/profile/verify/:code" element={<ProfileVerificationPage />} />
          <Route path="/scanner/:token" element={<MobileScanner />} />
          <Route path="/f/:code" element={<SharedFilePage />} />
          <Route path="/review/:code" element={<ContentReviewPage />} />
          <Route path="/tracker/v/:token" element={<OrganizationProvider><PublicTrackerPage mode="view" /></OrganizationProvider>} />
          <Route path="/tracker/e/:token" element={<OrganizationProvider><PublicTrackerPage mode="edit" /></OrganizationProvider>} />
          <Route path="/onboard/crew/:token" element={<CrewOnboardingForm />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/style-preview" element={<StylePreview />} />

          {/* All other routes — loads MUI + auth + everything on demand */}
          <Route path="/*" element={<AuthenticatedApp />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
